import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SpeakerTimelineHelpDialog } from "@/components/shared/SpeakerTimelineHelpDialog";
import { formatTimestamp, sanitizeSpeakerDisplayName } from "@/lib/utils";
import { ArrowUpRight, CircleHelp, Minimize2 } from "lucide-react";
import { toast } from "sonner";

const palette = [
  {
    range: "bg-sky-500 border-sky-400",
    chip: "border-sky-500/30 bg-sky-500/10 text-sky-100 hover:bg-sky-500/20",
    chipActive: "border-sky-300 bg-sky-500/25 text-white shadow-[0_0_0_1px_rgba(125,211,252,0.35),0_0_24px_rgba(14,165,233,0.35)]",
    glow: "border-sky-300/80 bg-sky-400/15 shadow-[0_0_28px_rgba(56,189,248,0.35)]",
    rangeActive: "border-sky-200 bg-sky-400 shadow-[0_0_18px_rgba(56,189,248,0.65)]",
  },
  {
    range: "bg-emerald-500 border-emerald-400",
    chip: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20",
    chipActive: "border-emerald-300 bg-emerald-500/25 text-white shadow-[0_0_0_1px_rgba(110,231,183,0.35),0_0_24px_rgba(16,185,129,0.35)]",
    glow: "border-emerald-300/80 bg-emerald-400/15 shadow-[0_0_28px_rgba(52,211,153,0.35)]",
    rangeActive: "border-emerald-200 bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.65)]",
  },
  {
    range: "bg-amber-500 border-amber-400",
    chip: "border-amber-500/30 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20",
    chipActive: "border-amber-300 bg-amber-500/25 text-white shadow-[0_0_0_1px_rgba(252,211,77,0.35),0_0_24px_rgba(245,158,11,0.35)]",
    glow: "border-amber-300/80 bg-amber-400/15 shadow-[0_0_28px_rgba(251,191,36,0.35)]",
    rangeActive: "border-amber-200 bg-amber-400 shadow-[0_0_18px_rgba(251,191,36,0.65)]",
  },
  {
    range: "bg-rose-500 border-rose-400",
    chip: "border-rose-500/30 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20",
    chipActive: "border-rose-300 bg-rose-500/25 text-white shadow-[0_0_0_1px_rgba(253,164,175,0.35),0_0_24px_rgba(244,63,94,0.35)]",
    glow: "border-rose-300/80 bg-rose-400/15 shadow-[0_0_28px_rgba(251,113,133,0.35)]",
    rangeActive: "border-rose-200 bg-rose-400 shadow-[0_0_18px_rgba(251,113,133,0.65)]",
  },
  {
    range: "bg-fuchsia-500 border-fuchsia-400",
    chip: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-100 hover:bg-fuchsia-500/20",
    chipActive: "border-fuchsia-300 bg-fuchsia-500/25 text-white shadow-[0_0_0_1px_rgba(240,171,252,0.35),0_0_24px_rgba(217,70,239,0.35)]",
    glow: "border-fuchsia-300/80 bg-fuchsia-400/15 shadow-[0_0_28px_rgba(232,121,249,0.35)]",
    rangeActive: "border-fuchsia-200 bg-fuchsia-400 shadow-[0_0_18px_rgba(232,121,249,0.65)]",
  },
  {
    range: "bg-cyan-500 border-cyan-400",
    chip: "border-cyan-500/30 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/20",
    chipActive: "border-cyan-300 bg-cyan-500/25 text-white shadow-[0_0_0_1px_rgba(103,232,249,0.35),0_0_24px_rgba(6,182,212,0.35)]",
    glow: "border-cyan-300/80 bg-cyan-400/15 shadow-[0_0_28px_rgba(34,211,238,0.35)]",
    rangeActive: "border-cyan-200 bg-cyan-400 shadow-[0_0_18px_rgba(34,211,238,0.65)]",
  },
  {
    range: "bg-lime-500 border-lime-400",
    chip: "border-lime-500/30 bg-lime-500/10 text-lime-100 hover:bg-lime-500/20",
    chipActive: "border-lime-300 bg-lime-500/25 text-white shadow-[0_0_0_1px_rgba(190,242,100,0.35),0_0_24px_rgba(132,204,22,0.35)]",
    glow: "border-lime-300/80 bg-lime-400/15 shadow-[0_0_28px_rgba(163,230,53,0.35)]",
    rangeActive: "border-lime-200 bg-lime-400 shadow-[0_0_18px_rgba(163,230,53,0.65)]",
  },
];

const getColorTheme = (speakerId = "", index = 0) => {
  const numericMatch = String(speakerId).match(/(\d+)$/);
  const numericIndex = numericMatch ? Number.parseInt(numericMatch[1], 10) - 1 : index;
  return palette[((numericIndex % palette.length) + palette.length) % palette.length];
};

const getProfileName = (profile, language) => sanitizeSpeakerDisplayName(
  profile?.custom_name || profile?.suggested_name || profile?.label || (language === "es" ? "Speaker" : "Speaker"),
  language === "es" ? "Speaker" : "Speaker",
);

const getRangeKey = (range) => `${range?.speaker_id || "speaker"}-${range?.startIndex || 0}-${range?.endIndex || 0}`;

const getRangeCenter = (range) => ((Number(range?.start || 0) + Number(range?.end || 0)) / 2);

const resolveClosestRangeForHint = (ranges = [], hint = null) => {
  if (!hint?.speakerId) return null;
  const candidateRanges = ranges.filter((range) => range.speaker_id === hint.speakerId);
  if (!candidateRanges.length) return null;

  return candidateRanges.reduce((best, current) => {
    if (!best) return current;
    return Math.abs(getRangeCenter(current) - getRangeCenter(hint)) < Math.abs(getRangeCenter(best) - getRangeCenter(hint))
      ? current
      : best;
  }, null);
};

const getSegmentSpeakerId = (segment) => {
  const speakerId = segment?.speaker_id || null;
  if (!speakerId || speakerId === "unknown") return null;
  return speakerId;
};

const buildRanges = (segments = []) => {
  const ranges = [];
  let current = null;
  segments.forEach((segment, index) => {
    const speakerId = getSegmentSpeakerId(segment);
    if (!speakerId) return;
    const start = Number(segment?.start || 0);
    const end = Math.max(start, Number(segment?.end || start));
    if (!current || current.speaker_id !== speakerId || Math.abs(start - current.end) > 0.6) {
      current = {
        speaker_id: speakerId,
        start,
        end,
        startIndex: index,
        endIndex: index,
        manual: Boolean(segment?.speaker_manual_override),
      };
      ranges.push(current);
      return;
    }
    current.end = end;
    current.endIndex = index;
    current.manual = current.manual || Boolean(segment?.speaker_manual_override);
  });
  return ranges;
};

const buildWaveformBars = (segments = [], totalDuration = 1, barCount = 72) => {
  if (!segments.length) {
    return Array.from({ length: barCount }).map((_, index) => ({ id: `empty-${index}`, height: 22 + ((index * 13) % 30) }));
  }

  const bucketSize = totalDuration / barCount;
  return Array.from({ length: barCount }).map((_, index) => {
    const bucketStart = index * bucketSize;
    const bucketEnd = bucketStart + bucketSize;
    const relatedSegments = segments.filter((segment) => {
      const segmentStart = Number(segment?.start || 0);
      const segmentEnd = Math.max(segmentStart, Number(segment?.end || segmentStart));
      return segmentEnd >= bucketStart && segmentStart <= bucketEnd;
    });

    const density = relatedSegments.reduce((sum, segment) => {
      const duration = Math.max(0.4, Number(segment?.end || 0) - Number(segment?.start || 0));
      return sum + Math.min(1.8, duration / 3) + Math.min(1.4, ((segment?.text || "").length || 0) / 220);
    }, 0);

    const normalized = Math.min(1, density / 3.2);
    return {
      id: `bar-${index}`,
      height: 18 + normalized * 58,
    };
  });
};

export const SpeakerTimelineEditor = ({
  segments = [],
  profiles = [],
  language = "es",
  onAssignRange,
  onDeleteSpeaker,
  onIdentifySpeakers,
  onSeek,
  loading = false,
  deletingSpeakerId = "",
  identifyingSpeakers = false,
  identifyingSpeakersProgress = 0,
  identifyingSpeakersMessage = "",
}) => {
  const copy = useMemo(() => language === "es" ? {
    title: "Speakers (Beta)",
    assignRange: "Marcar speaker",
    clearRange: "Limpiar",
    selectedRange: "Rango",
    description: "Selecciona el área donde habla el speaker y dale Marcar speaker para guardar. Esta función está en Beta, así que conviene revisarla manualmente.",
    editHint: "Para ajustar un tramo existente: arrastra el centro para moverlo o los extremos para agrandarlo.",
    speakerName: "Nombre del speaker",
    noSpeaker: "Selecciona o crea un speaker",
    addNameError: "Agrega nombre al speaker",
    deleteTitle: "¿Seguro que quieres borrar este speaker?",
    deleteDescription: "Si continúas, este speaker se eliminará y sus tramos volverán a verse como Speaker.",
    deleteConfirm: "Sí",
    deleteCancel: "No",
    deleteClose: "Cerrar",
    identifySpeakers: "Identificar speakers (Beta)",
    identifyingSpeakers: "Identificando speakers (Beta)...",
    identifyingDescription: "La IA está leyendo la transcripción y detectando quién habla para poblar la barra automáticamente. Como está en Beta, puede requerir ajustes manuales.",
    identifyingStageFallback: "Leyendo el juicio completo para entender quién habla, cuándo vuelve a intervenir y cómo se relacionan los intervinientes.",
    reidentifyTitle: "¿Estás seguro?",
    reidentifyDescription: "Se eliminarán todos los speakers actuales y se volverán a identificar desde cero.",
    reidentifyConfirm: "Sí, volver a identificar",
    openEditor: "Abrir edición",
    closeEditor: "Cerrar edición",
    editRangeButton: "Editar tramo",
    editRangeActive: "Edición de tramo activa",
  } : {
    title: "Speakers (Beta)",
    assignRange: "Mark speaker",
    clearRange: "Clear",
    selectedRange: "Range",
    description: "Select the area where the speaker is talking, then click Mark speaker to save it. This feature is in Beta, so a manual review is still recommended.",
    editHint: "To adjust an existing range: drag its center to move it or drag either edge to resize it.",
    speakerName: "Speaker name",
    noSpeaker: "Select or create a speaker",
    addNameError: "Add a name for the speaker",
    deleteTitle: "Are you sure you want to delete this speaker?",
    deleteDescription: "If you continue, this speaker will be removed and its segments will go back to Speaker.",
    deleteConfirm: "Yes",
    deleteCancel: "No",
    deleteClose: "Close",
    identifySpeakers: "Identify speakers (Beta)",
    identifyingSpeakers: "Identifying speakers (Beta)...",
    identifyingDescription: "AI is reading the transcript and detecting who speaks so it can fill the timeline automatically. Because it is in Beta, manual adjustments may still be needed.",
    identifyingStageFallback: "Reading the full hearing to understand who is speaking, when they return, and how the participants relate to each other.",
    reidentifyTitle: "Are you sure?",
    reidentifyDescription: "All current speakers will be removed and identified again from scratch.",
    reidentifyConfirm: "Yes, re-identify",
    openEditor: "Open editor",
    closeEditor: "Close editor",
    editRangeButton: "Edit range",
    editRangeActive: "Range editing enabled",
  }, [language]);

  const totalDuration = useMemo(() => {
    const lastSegment = segments[segments.length - 1];
    return Math.max(1, Number(lastSegment?.end || lastSegment?.start || 1));
  }, [segments]);

  const boundedIdentifyingProgress = Math.max(2, Math.min(100, Number(identifyingSpeakersProgress || 0)));
  const identifyingStatusMessage = identifyingSpeakersMessage || copy.identifyingStageFallback;

  const presentSpeakerIds = useMemo(() => Array.from(new Set(
    segments
      .map((segment) => getSegmentSpeakerId(segment))
      .filter(Boolean)
  )), [segments]);

  const availableProfiles = useMemo(() => profiles.map((profile, index) => ({
    ...profile,
    display_name: getProfileName(profile, language),
    color_theme: getColorTheme(profile?.speaker_id, index),
  })).filter((profile) => {
    const hasDetectedRange = presentSpeakerIds.includes(profile.speaker_id);
    const hasNamedIdentity = Boolean((profile?.custom_name || profile?.suggested_name || "").trim());
    return hasDetectedRange || hasNamedIdentity;
  }), [language, presentSpeakerIds, profiles]);

  const preferredActiveSpeakerId = useMemo(
    () => availableProfiles.find((profile) => presentSpeakerIds.includes(profile.speaker_id))?.speaker_id || availableProfiles[0]?.speaker_id || "",
    [availableProfiles, presentSpeakerIds],
  );

  const [selectedSpeakerId, setSelectedSpeakerId] = useState(preferredActiveSpeakerId);
  const [activeRangeKey, setActiveRangeKey] = useState("");
  const [speakerNameInput, setSpeakerNameInput] = useState("");
  const [selection, setSelection] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [hoverPreview, setHoverPreview] = useState(null);
  const [speakerPendingDelete, setSpeakerPendingDelete] = useState(null);
  const [identifyConfirmOpen, setIdentifyConfirmOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [rangeEditState, setRangeEditState] = useState(null);
  const [rangeFocusHint, setRangeFocusHint] = useState(null);
  const [rangeEditingEnabled, setRangeEditingEnabled] = useState(false);
  const [editorExpanded, setEditorExpanded] = useState(false);
  const barRef = useRef(null);
  const hasAutoExpandedRef = useRef(false);

  useEffect(() => {
    if (!selectedSpeakerId && preferredActiveSpeakerId) {
      setSelectedSpeakerId(preferredActiveSpeakerId);
    }
  }, [preferredActiveSpeakerId, selectedSpeakerId]);

  useEffect(() => {
    if (!selectedSpeakerId) return;
    const exists = availableProfiles.some((profile) => profile.speaker_id === selectedSpeakerId);
    if (!exists && preferredActiveSpeakerId) {
      setSelectedSpeakerId(preferredActiveSpeakerId);
    }
  }, [availableProfiles, preferredActiveSpeakerId, selectedSpeakerId]);

  const timelineRanges = useMemo(() => buildRanges(segments).map((range, index) => ({
    ...range,
    left: `${(range.start / totalDuration) * 100}%`,
    width: `${Math.max(0.7, ((range.end - range.start) / totalDuration) * 100)}%`,
    color_theme: getColorTheme(range.speaker_id, index),
  })), [segments, totalDuration]);

  useEffect(() => {
    if (hasAutoExpandedRef.current) return;
    if (availableProfiles.length === 0 && timelineRanges.length === 0) return;
    setEditorExpanded(true);
    hasAutoExpandedRef.current = true;
  }, [availableProfiles.length, timelineRanges.length]);

  const highlightedRanges = useMemo(
    () => timelineRanges.filter((range) => range.speaker_id === selectedSpeakerId),
    [selectedSpeakerId, timelineRanges],
  );

  const selectedExistingRange = useMemo(
    () => timelineRanges.find((range) => getRangeKey(range) === activeRangeKey) || null,
    [activeRangeKey, timelineRanges],
  );

  const hintedRange = useMemo(
    () => resolveClosestRangeForHint(timelineRanges, rangeFocusHint),
    [rangeFocusHint, timelineRanges],
  );

  const activeRange = useMemo(
    () => selectedExistingRange || hintedRange || highlightedRanges[0] || null,
    [highlightedRanges, hintedRange, selectedExistingRange],
  );

  const timelineBoundaries = useMemo(() => {
    const boundaries = new Set([0, totalDuration]);
    segments.forEach((segment) => {
      boundaries.add(Number(segment?.start || 0));
      boundaries.add(Number(segment?.end || segment?.start || 0));
    });
    return Array.from(boundaries).filter((value) => Number.isFinite(value)).sort((left, right) => left - right);
  }, [segments, totalDuration]);

  const waveformBars = useMemo(() => buildWaveformBars(segments, totalDuration), [segments, totalDuration]);

  const buildRangeStyle = (start, end, minWidth = 0.7) => ({
    left: `${(start / totalDuration) * 100}%`,
    width: `${Math.max(minWidth, ((end - start) / totalDuration) * 100)}%`,
  });

  const toSeconds = (clientX) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Number((ratio * totalDuration).toFixed(2));
  };

  const snapToBoundary = (seconds, fallback = 0) => {
    if (!timelineBoundaries.length) return Number(seconds || fallback || 0);
    return timelineBoundaries.reduce((closest, current) => (
      Math.abs(current - seconds) < Math.abs(closest - seconds) ? current : closest
    ), timelineBoundaries[0]);
  };

  const getPointerPreview = (clientX) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return {
      seconds: Number((ratio * totalDuration).toFixed(2)),
      leftPercent: Number((ratio * 100).toFixed(2)),
    };
  };

  const normalizeEditableRange = (start, end, preserveDuration = false, originalRange = null) => {
    let nextStart = Math.max(0, Math.min(start, totalDuration));
    let nextEnd = Math.max(0, Math.min(end, totalDuration));

    if (preserveDuration && originalRange) {
      const duration = Math.max(0.1, originalRange.end - originalRange.start);
      if (nextStart < 0) {
        nextStart = 0;
        nextEnd = duration;
      }
      if (nextEnd > totalDuration) {
        nextEnd = totalDuration;
        nextStart = Math.max(0, totalDuration - duration);
      }
    }

    nextStart = snapToBoundary(nextStart, originalRange?.start || 0);
    nextEnd = snapToBoundary(nextEnd, originalRange?.end || nextStart);

    if (nextEnd <= nextStart) {
      const currentIndex = timelineBoundaries.findIndex((value) => value === nextStart);
      const nextBoundary = timelineBoundaries[currentIndex + 1];
      const previousBoundary = timelineBoundaries[currentIndex - 1];
      if (nextBoundary !== undefined) {
        nextEnd = nextBoundary;
      } else if (previousBoundary !== undefined) {
        nextStart = previousBoundary;
      }
    }

    return {
      start: Number(nextStart.toFixed(2)),
      end: Number(nextEnd.toFixed(2)),
    };
  };

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (rangeEditState) return;
    const preview = getPointerPreview(event.clientX);
    if (!preview) return;
    const seconds = preview.seconds;
    setDragState({ anchor: seconds, current: seconds, moved: false });
    setHoverPreview(preview);
    onSeek?.(seconds);
  };

  const handlePointerMove = (event) => {
    const preview = getPointerPreview(event.clientX);
    if (preview) {
      setHoverPreview(preview);
    }
    if (rangeEditState) {
      return;
    }
    if (!dragState || !preview) return;
    const seconds = preview.seconds;
    setDragState((current) => current ? ({
      ...current,
      current: seconds,
      moved: current.moved || Math.abs(seconds - current.anchor) > 0.5,
    }) : current);
    onSeek?.(seconds);
  };

  const handlePointerUp = (event) => {
    if (rangeEditState) {
      return;
    }
    if (!dragState) return;
    const preview = getPointerPreview(event.clientX);
    const seconds = preview?.seconds ?? toSeconds(event.clientX);
    if (preview) {
      setHoverPreview(preview);
    }
    const moved = dragState.moved || Math.abs(seconds - dragState.anchor) > 0.5;
    if (!moved) {
      setSelection(null);
      onSeek?.(seconds);
      setDragState(null);
      return;
    }

    setSelection(normalizeEditableRange(
      Math.min(dragState.anchor, seconds),
      Math.max(dragState.anchor, seconds),
    ));
    onSeek?.(seconds);
    setDragState(null);
  };

  const displayedSelection = rangeEditState?.draftRange || (dragState ? {
    start: Math.min(dragState.anchor, dragState.current),
    end: Math.max(dragState.anchor, dragState.current),
  } : null) || selection || selectedExistingRange || hintedRange;

  const assignableSelection = selection;

  const creationSelection = dragState ? {
    start: Math.min(dragState.anchor, dragState.current),
    end: Math.max(dragState.anchor, dragState.current),
  } : selection;

  const selectionStyle = creationSelection ? {
    left: `${(creationSelection.start / totalDuration) * 100}%`,
    width: `${Math.max(0.45, ((creationSelection.end - creationSelection.start) / totalDuration) * 100)}%`,
  } : null;

  const handleAssignRange = async () => {
    if (!assignableSelection) return;
    const trimmedName = speakerNameInput.trim();
    if (!trimmedName) {
      toast.error(copy.addNameError);
      return;
    }
    await onAssignRange?.({
      start_time: assignableSelection.start,
      end_time: assignableSelection.end,
      speaker_id: null,
      create_new: true,
      custom_name: trimmedName,
    });
    setSelection(null);
    setSelectedSpeakerId("");
    setSpeakerNameInput("");
  };

  const startRangeEdit = (event, range, mode) => {
    event.stopPropagation();
    event.preventDefault();
    const preview = getPointerPreview(event.clientX);
    const originalRange = { start: range.start, end: range.end };
    setSelection(null);
    setRangeFocusHint({ speakerId: range.speaker_id, start: range.start, end: range.end });
    setSelectedSpeakerId(range.speaker_id);
    setActiveRangeKey(getRangeKey(range));
    setRangeEditState({
      mode,
      rangeKey: getRangeKey(range),
      speakerId: range.speaker_id,
      originalRange,
      draftRange: originalRange,
      pointerAnchor: preview?.seconds ?? range.start,
      moved: false,
    });
  };

  const handleDeleteSpeaker = (event, speakerId) => {
    event.stopPropagation();
    setSpeakerPendingDelete(speakerId);
  };

  const confirmDeleteSpeaker = async () => {
    if (!speakerPendingDelete) return;
    await onDeleteSpeaker?.(speakerPendingDelete);
    setSpeakerPendingDelete(null);
  };

  const handleIdentifySpeakersClick = () => {
    if (availableProfiles.length > 0) {
      setIdentifyConfirmOpen(true);
      return;
    }
    onIdentifySpeakers?.();
  };

  const confirmIdentifySpeakers = async () => {
    const speakerIds = availableProfiles.map((profile) => profile.speaker_id);
    setIdentifyConfirmOpen(false);
    setSelection(null);
    setRangeEditingEnabled(false);
    setSelectedSpeakerId("");
    setSpeakerNameInput("");
    for (const speakerId of speakerIds) {
      await onDeleteSpeaker?.(speakerId);
    }
    await onIdentifySpeakers?.();
  };

  const toggleEditorExpanded = () => {
    setEditorExpanded((current) => {
      const next = !current;
      if (!next) {
        setRangeEditingEnabled(false);
        setSelection(null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (selectedExistingRange) return;
    if (!hintedRange) return;
    setActiveRangeKey(getRangeKey(hintedRange));
  }, [hintedRange, selectedExistingRange]);

  useEffect(() => {
    if (!selectedSpeakerId) {
      setActiveRangeKey("");
      return;
    }
    const nextActiveRange = highlightedRanges[0];
    if (!nextActiveRange) {
      setActiveRangeKey("");
      return;
    }
    if (!selectedExistingRange || selectedExistingRange.speaker_id !== selectedSpeakerId) {
      setActiveRangeKey(getRangeKey(nextActiveRange));
    }
  }, [highlightedRanges, selectedExistingRange, selectedSpeakerId]);

  useEffect(() => {
    if (!rangeEditState) return undefined;

    const handleWindowPointerMove = (event) => {
      const preview = getPointerPreview(event.clientX);
      if (preview) {
        setHoverPreview(preview);
      }
      const pointerSeconds = preview?.seconds ?? toSeconds(event.clientX);
      const delta = pointerSeconds - rangeEditState.pointerAnchor;
      const originalRange = rangeEditState.originalRange;
      let nextRange = originalRange;
      if (rangeEditState.mode === "move") {
        nextRange = normalizeEditableRange(
          originalRange.start + delta,
          originalRange.end + delta,
          true,
          originalRange,
        );
      }
      if (rangeEditState.mode === "resize-start") {
        nextRange = normalizeEditableRange(pointerSeconds, originalRange.end, false, originalRange);
      }
      if (rangeEditState.mode === "resize-end") {
        nextRange = normalizeEditableRange(originalRange.start, pointerSeconds, false, originalRange);
      }
      setRangeEditState((current) => current ? ({
        ...current,
        draftRange: nextRange,
        moved: current.moved || Math.abs(delta) > 0.1,
      }) : current);
    };

    const handleWindowPointerUp = () => {
      setRangeEditState((current) => {
        if (!current) return current;
        const finalRange = current.draftRange || current.originalRange;
        const hadMeaningfulMove = current.moved && Math.abs(finalRange.end - finalRange.start) > 0.05;
        if (!hadMeaningfulMove) {
          setRangeFocusHint({ speakerId: current.speakerId, start: current.originalRange.start, end: current.originalRange.end });
          setSelectedSpeakerId(current.speakerId);
          setActiveRangeKey(current.rangeKey);
          onSeek?.(current.originalRange.start);
          return null;
        }
        onAssignRange?.({
          start_time: finalRange.start,
          end_time: finalRange.end,
          speaker_id: current.speakerId,
          create_new: false,
          original_start_time: current.originalRange.start,
          original_end_time: current.originalRange.end,
          edit_mode: current.mode === "move" ? "move" : "resize",
        });
        setRangeFocusHint({ speakerId: current.speakerId, start: finalRange.start, end: finalRange.end });
        setSelection(null);
        return null;
      });
    };

    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", handleWindowPointerUp);
    window.addEventListener("mousemove", handleWindowPointerMove);
    window.addEventListener("mouseup", handleWindowPointerUp);
    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove);
      window.removeEventListener("pointerup", handleWindowPointerUp);
      window.removeEventListener("mousemove", handleWindowPointerMove);
      window.removeEventListener("mouseup", handleWindowPointerUp);
    };
  }, [onAssignRange, onSeek, rangeEditState]);

  return (
    <Card className="relative border-border bg-card/95 px-3 py-3 sm:px-4" data-testid="speaker-timeline-editor">
      {identifyingSpeakers ? (
        <div className="absolute inset-0 z-40 flex items-center justify-center rounded-xl bg-background/75 backdrop-blur-[2px]" data-testid="speaker-timeline-identifying-overlay">
          <div className="flex max-w-sm flex-col items-center gap-3 rounded-2xl border border-border/70 bg-card/95 px-5 py-4 text-center shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
            <div className="flex items-end gap-1" data-testid="speaker-timeline-identifying-bars">
              <span className="h-3 w-2 animate-pulse rounded-full bg-sky-400" />
              <span className="h-5 w-2 animate-pulse rounded-full bg-emerald-400 [animation-delay:120ms]" />
              <span className="h-7 w-2 animate-pulse rounded-full bg-amber-400 [animation-delay:240ms]" />
              <span className="h-5 w-2 animate-pulse rounded-full bg-rose-400 [animation-delay:360ms]" />
              <span className="h-3 w-2 animate-pulse rounded-full bg-cyan-400 [animation-delay:480ms]" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground" data-testid="speaker-timeline-identifying-title">{copy.identifyingSpeakers}</p>
              <p className="text-xs leading-5 text-muted-foreground" data-testid="speaker-timeline-identifying-description">{copy.identifyingDescription}</p>
            </div>
            <div className="w-full space-y-2" data-testid="speaker-timeline-identifying-progress-card">
              <div className="flex items-center justify-between text-[11px] font-medium text-foreground/90">
                <span data-testid="speaker-timeline-identifying-progress-label">{boundedIdentifyingProgress}%</span>
                <span className="rounded-full border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-sky-200">Voxara AI</span>
              </div>
              <div className="relative overflow-hidden rounded-full border border-white/10 bg-slate-950/60 p-[2px]">
                <div className="absolute inset-0 animate-pulse bg-[radial-gradient(circle_at_left,rgba(56,189,248,0.22),transparent_45%),radial-gradient(circle_at_center,rgba(16,185,129,0.18),transparent_45%),radial-gradient(circle_at_right,rgba(245,158,11,0.18),transparent_40%)]" />
                <Progress
                  value={boundedIdentifyingProgress}
                  className="relative h-3 bg-white/10"
                  data-testid="speaker-timeline-identifying-progress-bar"
                />
                <div
                  className="pointer-events-none absolute inset-y-0 left-0 animate-[pulse_1.8s_ease-in-out_infinite] bg-[linear-gradient(90deg,rgba(56,189,248,0.0),rgba(56,189,248,0.22),rgba(16,185,129,0.28),rgba(245,158,11,0.22),rgba(56,189,248,0.0))]"
                  style={{ width: `${boundedIdentifyingProgress}%` }}
                />
              </div>
              <p className="text-[11px] leading-5 text-slate-300" data-testid="speaker-timeline-identifying-status-message">{identifyingStatusMessage}</p>
            </div>
          </div>
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="space-y-1">
            <Badge variant="outline" data-testid="speaker-timeline-editor-badge">{copy.title}</Badge>
            {editorExpanded ? (
              <p className="max-w-xl text-[11px] leading-5 text-muted-foreground" data-testid="speaker-timeline-editor-description">
                {copy.description}
              </p>
            ) : null}
            {editorExpanded && activeRange && rangeEditingEnabled ? (
              <p className="max-w-xl text-[11px] leading-5 text-primary/80" data-testid="speaker-timeline-edit-hint">
                {copy.editHint}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setHelpDialogOpen(true)}
              className="speaker-edit-cta h-8 px-3 text-xs"
              data-testid="speaker-timeline-help-button"
            >
              <CircleHelp className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {language === "es" ? "Cómo funciona" : "How it works"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={toggleEditorExpanded}
              className="speaker-edit-cta h-8 px-3 text-xs"
              data-testid="speaker-timeline-editor-collapse-toggle-button"
            >
              {editorExpanded ? (
                <Minimize2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ArrowUpRight className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              )}
              {editorExpanded ? copy.closeEditor : copy.openEditor}
            </Button>
            {editorExpanded ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setRangeEditingEnabled((current) => !current)}
              disabled={loading || !activeRange}
              className="speaker-edit-cta h-8 px-3 text-xs"
              data-testid="speaker-timeline-edit-range-toggle-button"
            >
              {rangeEditingEnabled ? copy.editRangeActive : copy.editRangeButton}
            </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleIdentifySpeakersClick}
              disabled={loading || identifyingSpeakers || Boolean(deletingSpeakerId)}
              data-testid="speaker-timeline-identify-speakers-button"
            >
              {identifyingSpeakers ? copy.identifyingSpeakers : copy.identifySpeakers}
            </Button>
            <span data-testid="speaker-timeline-start-label">0:00</span>
            <span>·</span>
            <span data-testid="speaker-timeline-end-label">{formatTimestamp(totalDuration)}</span>
          </div>
        </div>

        {editorExpanded ? (
        <>
        <div className="flex flex-wrap items-center gap-2" data-testid="speaker-timeline-speaker-list">
          {availableProfiles.map((profile) => (
            <div
              key={profile.speaker_id}
              className="group relative"
              data-testid={`speaker-timeline-speaker-chip-${profile.speaker_id}`}
            >
              <button
                type="button"
                onClick={() => setSelectedSpeakerId(profile.speaker_id)}
                className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all duration-300 ${selectedSpeakerId === profile.speaker_id ? profile.color_theme.chipActive : profile.color_theme.chip}`}
                data-testid={`speaker-timeline-speaker-${profile.speaker_id}`}
              >
                {profile.display_name}
              </button>
              <button
                type="button"
                onClick={(event) => handleDeleteSpeaker(event, profile.speaker_id)}
                className="pointer-events-none invisible absolute -right-1 -top-1 rounded-full border border-red-500/50 bg-red-500/90 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white opacity-0 shadow-[0_6px_18px_rgba(239,68,68,0.35)] transition-all duration-200 group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 hover:scale-105 hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={deletingSpeakerId === profile.speaker_id || loading}
                data-testid={`speaker-timeline-delete-${profile.speaker_id}`}
                aria-label={`delete-${profile.speaker_id}`}
              >
                -
              </button>
            </div>
          ))}
        </div>

        <div
          ref={barRef}
          className="relative cursor-crosshair select-none overflow-hidden rounded-2xl border border-border bg-muted/20 py-2 [touch-action:none]"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={() => setHoverPreview(null)}
          data-testid="speaker-timeline-bar"
        >
          <div className="absolute inset-x-0 bottom-1 h-1 rounded-full bg-muted/70" />

          {hoverPreview ? (
            <>
              <div
                className="pointer-events-none absolute inset-y-1 z-20 w-px bg-white/40 transition-all duration-150"
                style={{ left: `${hoverPreview.leftPercent}%` }}
                data-testid="speaker-timeline-hover-line"
              />
              <div
                className="pointer-events-none absolute top-1 z-30 -translate-x-1/2 rounded-full border border-white/15 bg-background/95 px-2 py-1 text-[10px] font-semibold text-foreground shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-all duration-150"
                style={{ left: `${hoverPreview.leftPercent}%` }}
                data-testid="speaker-timeline-hover-tooltip"
              >
                {formatTimestamp(hoverPreview.seconds)}
              </div>
            </>
          ) : null}

          {highlightedRanges.map((range) => (
            (() => {
              const isEditingThisRange = Boolean(
                rangeEditState
                && activeRange
                && getRangeKey(activeRange) === getRangeKey(range)
                && rangeEditState.draftRange
              );
              const previewStyle = isEditingThisRange
                ? buildRangeStyle(rangeEditState.draftRange.start, rangeEditState.draftRange.end)
                : { left: range.left, width: range.width };

              return (
                <div
                  key={`highlight-${range.speaker_id}-${range.start}-${range.end}`}
                  className={`speaker-zone-highlight pointer-events-none absolute inset-y-1 rounded-2xl border bg-transparent transition-all duration-75 opacity-100 ${range.color_theme.glow}`}
                  style={previewStyle}
                  data-testid={`speaker-timeline-highlight-${range.speaker_id}-${range.startIndex}`}
                >
                  <div className="speaker-zone-highlight-core" />
                  <div className="speaker-zone-highlight-cap speaker-zone-highlight-cap-left" />
                  <div className="speaker-zone-highlight-cap speaker-zone-highlight-cap-right" />
                </div>
              );
            })()
          ))}

          <div className="flex h-14 items-end gap-[2px] px-[2px]">
            {waveformBars.map((bar) => (
              <div key={bar.id} className="flex-1 rounded-full bg-gradient-to-t from-primary/50 to-secondary/80" style={{ height: `${bar.height}%` }} />
            ))}
          </div>

          {timelineRanges.map((range) => (
            (() => {
              const isEditingThisRange = Boolean(
                rangeEditState
                && activeRange
                && getRangeKey(activeRange) === getRangeKey(range)
                && rangeEditState.draftRange
              );
              const previewStyle = isEditingThisRange
                ? buildRangeStyle(rangeEditState.draftRange.start, rangeEditState.draftRange.end)
                : { left: range.left, width: range.width };

              return (
            <div
              key={`${range.speaker_id}-${range.start}-${range.end}`}
              className="absolute bottom-0 flex h-9 items-end pb-1"
              style={previewStyle}
              data-testid={`speaker-timeline-range-wrapper-${range.speaker_id}-${range.startIndex}`}
            >
              {rangeEditingEnabled && activeRange && getRangeKey(activeRange) === getRangeKey(range) ? (
                <>
                  <button
                    type="button"
                    className={`absolute left-1/2 top-0 z-20 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-white/40 bg-background/95 text-[11px] text-foreground shadow transition-transform duration-200 hover:scale-105 active:scale-95 ${rangeEditState ? 'cursor-grabbing' : 'cursor-grab'}`}
                    onPointerDown={(event) => startRangeEdit(event, range, "move")}
                    data-testid={`speaker-timeline-range-handle-move-${range.speaker_id}-${range.startIndex}`}
                    aria-label={`move-${range.speaker_id}-${range.startIndex}`}
                  >
                    ↔
                  </button>
                  <button
                    type="button"
                    className="absolute -left-2 top-0 z-20 flex h-8 w-5 cursor-col-resize items-center justify-center rounded-full border border-white/40 bg-background/95 text-[10px] text-foreground shadow transition-transform duration-200 hover:scale-105 active:scale-95"
                    onPointerDown={(event) => startRangeEdit(event, range, "resize-start")}
                    data-testid={`speaker-timeline-range-handle-start-${range.speaker_id}-${range.startIndex}`}
                  >
                    ◂
                  </button>
                  <button
                    type="button"
                    className="absolute -right-2 top-0 z-20 flex h-8 w-5 cursor-col-resize items-center justify-center rounded-full border border-white/40 bg-background/95 text-[10px] text-foreground shadow transition-transform duration-200 hover:scale-105 active:scale-95"
                    onPointerDown={(event) => startRangeEdit(event, range, "resize-end")}
                    data-testid={`speaker-timeline-range-handle-end-${range.speaker_id}-${range.startIndex}`}
                  >
                    ▸
                  </button>
                </>
              ) : null}
              <button
                type="button"
                className={`absolute inset-x-0 bottom-0 rounded-full border transition-[height,box-shadow,transform] duration-300 [touch-action:none] ${selectedSpeakerId === range.speaker_id ? `h-3.5 cursor-grab ${range.color_theme.rangeActive}` : `h-2.5 cursor-pointer ${range.color_theme.range}`} ${rangeEditState && getRangeKey(activeRange) === getRangeKey(range) ? 'cursor-grabbing' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onSeek?.(range.start);
                  setSelectedSpeakerId(range.speaker_id);
                  setRangeFocusHint({ speakerId: range.speaker_id, start: range.start, end: range.end });
                  setActiveRangeKey(getRangeKey(range));
                  setSelection(null);
                }}
                onPointerDown={(event) => {
                  if (rangeEditingEnabled && activeRange && getRangeKey(activeRange) === getRangeKey(range)) {
                    startRangeEdit(event, range, "move");
                    return;
                  }
                  event.stopPropagation();
                }}
                data-testid={`speaker-timeline-range-${range.speaker_id}-${range.startIndex}`}
              />
            </div>
              );
            })()
          ))}

          {selectionStyle ? (
            <div
              className="absolute inset-y-2 rounded-xl border border-primary bg-primary/15"
              style={selectionStyle}
              data-testid="speaker-timeline-selection"
            />
          ) : null}
        </div>

        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="speaker-timeline-selection-summary">
            <span className="font-medium text-foreground">{copy.selectedRange}</span>
            <span>{displayedSelection ? `${formatTimestamp(displayedSelection.start)} → ${formatTimestamp(displayedSelection.end)}` : copy.noSpeaker}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-8 w-[150px]"
              value={speakerNameInput}
              onChange={(event) => setSpeakerNameInput(event.target.value.slice(0, 40))}
              data-testid="speaker-timeline-name-input"
              aria-label={copy.speakerName}
              placeholder={copy.speakerName}
            />
            <Button type="button" size="sm" onClick={handleAssignRange} disabled={loading || !assignableSelection} data-testid="speaker-timeline-assign-button">
              {copy.assignRange}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelection(null)} disabled={!selection || loading} data-testid="speaker-timeline-clear-selection-button">
              {copy.clearRange}
            </Button>
          </div>
        </div>
        </>
        ) : null}
      </div>

      <Dialog open={Boolean(speakerPendingDelete)} onOpenChange={(open) => !open && setSpeakerPendingDelete(null)}>
        <DialogContent data-testid="speaker-timeline-delete-confirmation-dialog">
          <DialogHeader>
            <DialogTitle data-testid="speaker-timeline-delete-confirmation-title">{copy.deleteTitle}</DialogTitle>
            <DialogDescription data-testid="speaker-timeline-delete-confirmation-description">{copy.deleteDescription}</DialogDescription>
          </DialogHeader>
          <div className="absolute right-4 top-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSpeakerPendingDelete(null)}
              data-testid="speaker-timeline-delete-confirmation-close-button"
            >
              {copy.deleteClose}
            </Button>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSpeakerPendingDelete(null)}
              data-testid="speaker-timeline-delete-confirmation-no-button"
            >
              {copy.deleteCancel}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDeleteSpeaker}
              disabled={Boolean(deletingSpeakerId) || loading}
              data-testid="speaker-timeline-delete-confirmation-yes-button"
            >
              {copy.deleteConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={identifyConfirmOpen} onOpenChange={setIdentifyConfirmOpen}>
        <DialogContent data-testid="speaker-timeline-reidentify-confirmation-dialog">
          <DialogHeader>
            <DialogTitle data-testid="speaker-timeline-reidentify-confirmation-title">{copy.reidentifyTitle}</DialogTitle>
            <DialogDescription data-testid="speaker-timeline-reidentify-confirmation-description">{copy.reidentifyDescription}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIdentifyConfirmOpen(false)}
              data-testid="speaker-timeline-reidentify-cancel-button"
            >
              {copy.deleteCancel}
            </Button>
            <Button
              type="button"
              onClick={confirmIdentifySpeakers}
              className="speaker-edit-cta"
              data-testid="speaker-timeline-reidentify-confirm-button"
            >
              {copy.reidentifyConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SpeakerTimelineHelpDialog
        language={language}
        open={helpDialogOpen}
        onOpenChange={setHelpDialogOpen}
      />
    </Card>
  );
};